import {
  DateFormatter,
  fromDate,
  getLocalTimeZone,
  now
} from "@internationalized/date";
import { ConversationEscalationResponse } from "@9amhealth/openapi";
import { getSupportedUserLocale } from "src/lib/i18next";
import translate from "src/lib/translate";
import type { LeapChatMessage } from "./types";

const MS_PER_MINUTE = 60_000;
const MS_PER_HOUR = 3_600_000;
const MS_PER_DAY = 86_400_000;
const MS_PER_WEEK = 604_800_000;
const MINUTES_PER_HOUR = 60;
const HOURS_PER_DAY = 24;
const DAYS_PER_WEEK = 7;

const formatRelativeMs = (epochMs: number): string | null => {
  const diffMs = Date.now() - epochMs;
  const diffMin = Math.floor(diffMs / MS_PER_MINUTE);
  const diffHr = Math.floor(diffMs / MS_PER_HOUR);
  const diffDays = Math.floor(diffMs / MS_PER_DAY);

  if (diffMin < 1) {
    return translate("leapChat.time.justNow");
  }
  if (diffMin < MINUTES_PER_HOUR) {
    return translate("leapChat.time.minutesAgo", { count: diffMin });
  }
  if (diffHr < HOURS_PER_DAY) {
    return translate("leapChat.time.hoursAgo", { count: diffHr });
  }
  if (diffDays === 1) {
    return translate("leapChat.time.yesterday");
  }
  if (diffDays < DAYS_PER_WEEK) {
    return translate("leapChat.time.daysAgo", { count: diffDays });
  }

  return null;
};

export const formatMessageTime = (epochMs: number): string => {
  const relative = formatRelativeMs(epochMs);
  if (relative) {
    return relative;
  }

  const tz = getLocalTimeZone();
  const zdt = fromDate(new Date(epochMs), tz);
  const currentYear = now(tz).year;
  return new DateFormatter(getSupportedUserLocale(), {
    month: "short",
    day: "numeric",
    ...(zdt.year !== currentYear ? { year: "numeric" } : {})
  }).format(zdt.toDate());
};

export const formatRelativeTime = (dateStr: string): string => {
  const epochMs = new Date(dateStr).getTime();
  const relative = formatRelativeMs(epochMs);
  if (relative) {
    return relative;
  }

  const diffWeeks = Math.floor((Date.now() - epochMs) / MS_PER_WEEK);
  if (diffWeeks === 1) {
    return translate("leapChat.time.weeksAgo", { count: 1 });
  }
  return translate("leapChat.time.weeksAgoPlural", { count: diffWeeks });
};

export const formatConversationDate = (dateStr: string): string => {
  const tz = getLocalTimeZone();
  const zdt = fromDate(new Date(dateStr), tz);
  const epochMs = zdt.toDate().getTime();

  const time = new DateFormatter(getSupportedUserLocale(), {
    hour: "numeric",
    minute: "2-digit"
  }).format(zdt.toDate());

  const diffDays = Math.floor((Date.now() - epochMs) / MS_PER_DAY);
  if (diffDays === 0) {
    return translate("leapChat.time.todayAt", { time });
  }
  if (diffDays === 1) {
    return translate("leapChat.time.yesterdayAt", { time });
  }
  if (diffDays < DAYS_PER_WEEK) {
    return translate("leapChat.time.daysAgoAt", {
      count: diffDays,
      time
    });
  }

  const dateFormatted = new DateFormatter(getSupportedUserLocale(), {}).format(
    zdt.toDate()
  );
  return `${dateFormatted}, ${time}`;
};

export const normalizeMessageText = (text: string): string =>
  text
    .replace(/[^\S\n]+$/gm, "")
    .replace(/\n{3,}/g, "\n\n")
    .trim();

const hasMessageContent = (msg: LeapChatMessage): boolean =>
  Boolean(
    msg.text.trim() ||
    msg.toolCalls?.length ||
    msg.files?.length ||
    msg.senderName ||
    msg.senderDescription
  );

export const isEmptyNonLocalMessage = (msg: LeapChatMessage): boolean => {
  if (msg.isLocal || msg.localId) {
    return false;
  }
  return !hasMessageContent(msg);
};

export const isEmptyMessage = (msg: LeapChatMessage): boolean => {
  if (msg.error) {
    return false;
  }
  return !hasMessageContent(msg);
};

/** Returns the max updatedAt (epoch ms) of RESOLVED escalations to gate stale escalation offers older than the latest resolution. */
export const getLatestResolvedEscalationAt = (
  escalations: ConversationEscalationResponse[]
): number | null => {
  const resolved = escalations.filter(
    (e) => e.status === ConversationEscalationResponse.status.RESOLVED
  );
  if (resolved.length === 0) {
    return null;
  }
  return Math.max(...resolved.map((e) => new Date(e.updatedAt).getTime()));
};

/** Escalation prompt buttons show only when a prompt exists, the conversation is not actively escalated, and the message is not older than the latest resolved escalation. */
export const shouldShowEscalationPrompt = (params: {
  hasPrompt: boolean;
  isEscalated: boolean;
  messageCreatedAt: number;
  latestResolvedEscalationAt: number | null;
}): boolean => {
  const {
    hasPrompt,
    isEscalated,
    messageCreatedAt,
    latestResolvedEscalationAt
  } = params;
  const isBeforeLatestResolved =
    latestResolvedEscalationAt !== null &&
    messageCreatedAt < latestResolvedEscalationAt;
  return hasPrompt && !isEscalated && !isBeforeLatestResolved;
};

export const sortMessages = (
  messages: LeapChatMessage[]
): LeapChatMessage[] => {
  return messages.slice().sort((a, b) => a.createdAt - b.createdAt);
};

export const messagesEqual = (
  a: LeapChatMessage[],
  b: LeapChatMessage[]
): boolean => {
  if (a.length !== b.length) {
    return false;
  }
  for (let i = 0; i < a.length; i++) {
    const am = a[i];
    const bm = b[i];
    if (
      am.id !== bm.id ||
      am.text !== bm.text ||
      am.createdAt !== bm.createdAt ||
      am.isLocal !== bm.isLocal ||
      am.unread !== bm.unread ||
      am.error?.code !== bm.error?.code ||
      am.error?.statusCode !== bm.error?.statusCode
    ) {
      return false;
    }
  }
  return true;
};
